/**
 * "Which tasks are in this area", answered in the browser.
 *
 * Every action's geometry is already loaded with the list, so drawing a polygon
 * and filtering the table against it needs no request at all. That matters
 * because the server-side version needs PostGIS geometry columns the action
 * tables do not have yet, and this covers the case people actually ask for: a
 * few hundred tasks on one incident, filtered interactively.
 *
 * The limit to be honest about is that it only sees what is loaded. It is a
 * view filter, not a query.
 */

import { booleanDisjoint, booleanPointInPolygon } from '@turf/turf';
import type { Feature, Geometry, Polygon, MultiPolygon } from 'geojson';
import {
  getActionCoordinates,
  getActionGeoJsonGeometry,
} from './actionGeolocation';

/**
 * Read defensively rather than requiring a `geolocation` field: older org
 * action rows (and some list payloads) still omit it, so a required field would
 * drop those rows from the spatial filter instead of treating them as
 * unlocated.
 */
function geolocationOf(action: object): unknown {
  return (action as { geolocation?: unknown }).geolocation;
}

/**
 * The area the user drew, in WGS84. It lives here rather than with the map
 * context so the table-side control does not have to import the map.
 */
export type ActionAreaFilter = {
  geometry: Polygon | MultiPolygon;
  /** What to call it in the UI, usually the reverse-geocoded address. */
  label: string;
};

/** An area filter only makes sense against something with an inside. */
export function isAreaGeometry(
  geometry: Geometry | null | undefined
): geometry is Polygon | MultiPolygon {
  return geometry?.type === 'Polygon' || geometry?.type === 'MultiPolygon';
}

function asFeature(geometry: Geometry): Feature {
  return { type: 'Feature', properties: {}, geometry };
}

/**
 * Whether an action falls inside the drawn area.
 *
 * A point action is a straightforward containment test. An action with its own
 * area is counted when the two areas overlap at all, since a task covering a
 * whole district is "in" a filter drawn over part of it. Actions with no
 * location are out: an area filter is a question about place, and they have no
 * answer.
 */
export function isActionInArea(
  action: object,
  area: Polygon | MultiPolygon
): boolean {
  const geolocation = geolocationOf(action);
  const geometry = getActionGeoJsonGeometry(geolocation);

  if (geometry && isAreaGeometry(geometry)) {
    try {
      // `booleanIntersects` is the readable spelling but is missing from turf 6's
      // type declarations; it is defined as the negation of disjointness anyway.
      return !booleanDisjoint(asFeature(geometry), asFeature(area));
    } catch {
      // Self-intersecting rings and other malformed geometry: fall through to
      // the centroid test rather than dropping the action silently.
    }
  }

  const coords = getActionCoordinates(geolocation);
  if (!coords) return false;

  try {
    return booleanPointInPolygon([coords.longitude, coords.latitude], area);
  } catch {
    return false;
  }
}

/** The subset of `actions` inside `area`; the whole list when there is no area. */
export function filterActionsInArea<T extends object>(
  actions: T[],
  area: Polygon | MultiPolygon | null | undefined
): T[] {
  if (!area) return actions;
  return actions.filter((action) => isActionInArea(action, area));
}

/** How many of these actions could ever match an area filter. */
export function countLocatedActions(actions: readonly object[]): number {
  return actions.filter((action) => {
    const geolocation = geolocationOf(action);
    return (
      getActionCoordinates(geolocation) != null ||
      isAreaGeometry(getActionGeoJsonGeometry(geolocation))
    );
  }).length;
}

export function describeAreaFilter(
  total: number,
  matched: number,
  located: number
): string {
  if (total === 0) return 'No tasks to filter';
  const unlocated = total - located;
  const base = `${matched} of ${located} located ${located === 1 ? 'task' : 'tasks'} in this area`;
  return unlocated > 0
    ? `${base}, ${unlocated} without a location hidden`
    : base;
}
